import { useContext, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { notification } from "antd";
import { AssessmentHttpService } from "@/modules/assessments/services/assessment-http.service";
import { isValidNumericParam } from "@/shared/utils/common.utils";
import { AssessmentContext } from "@/shared/contexts/assessment.context";
import { ASSESSMENT_ROUTE, CLIENT_ROUTE } from "@/routing/AppRouter/routes.constants";

export function useAssessmentHook(assessmentId?: number) {
  const { assessment, setAssessment } = useContext(AssessmentContext);
  if (!setAssessment) {
    throw new Error("Cannot use useAssessmentHook without AssessmentContext");
  }
  const { client_id: clientId } = useParams<{ client_id: string }>();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState<boolean>(true);

  // We use a mutable ref for 'navigate' because the 'navigate' function is not guaranteed to be stable between renders.
  // By referencing it via navigateRef.current, we avoid re-running effects unnecessarily when 'navigate' changes.
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;

  useEffect(() => {
    let cancelled = false;

    const navigateToAssessmentsList = () => {
      navigateRef.current(`/${CLIENT_ROUTE}/${clientId}/${ASSESSMENT_ROUTE}`);
    };

    const fetchAssessment = async () => {
      try {
        setIsLoading(true);
        const inviteAssessment = await AssessmentHttpService.getAssessmentByID(Number(assessmentId));
        if (cancelled) return;

        if (inviteAssessment) {
          setAssessment(inviteAssessment);
        }
      } catch (e) {
        if (cancelled) return;

        console.error(`get assessment error, ${e}`);
        notification.error({
          message: "Something went wrong",
          description: "We couldn't load this assessment. Please try again or select a different one.",
        });
        navigateToAssessmentsList();
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    const hasValidId = isValidNumericParam(assessmentId);

    if (!hasValidId) {
      notification.error({
        message: "Assessment not found",
        description: "The assessment you're looking for doesn't exist or the link may be incorrect.",
      });
      navigateToAssessmentsList();
      setIsLoading(false);
      return;
    }

    const loadedAssessmentId = assessment?.assessmentId;
    if (assessment && loadedAssessmentId === Number(assessmentId)) {
      setIsLoading(false);
      return;
    }

    fetchAssessment();

    return () => {
      cancelled = true;
    };
  }, [assessmentId, assessment, setAssessment, clientId]);

  return { assessment, isLoading, setAssessment };
}
